import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

import { db } from "../db";
import { syncService } from "../services/SyncService";
import { toPaise, toRupees } from "../utils/currency";

export default function PatientEditDialog({ open, onOpenChange, patient, userId }) {
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        age: "",
        gender: "male",
        notes: "",
        referral_doctor_id: "",
        referral_doctor_name: "",
        referral_doctor_phone: "",
        rule_type: "percent",
        fee_value: "",
        date_of_birth: "",
        address: "",
        ageInputMode: "age"
    });



    // Pre-fill form when patient changes
    useEffect(() => {
        if (patient) {
            setFormData({
                name: patient.name || "",
                phone: patient.phone || "",
                email: patient.email || "",
                age: patient.age?.toString() || "",
                gender: patient.gender || "male",
                notes: patient.notes || "",
                referral_doctor_id: patient.referral_doctor_id || "",
                referral_doctor_name: "",
                referral_doctor_phone: "",
                rule_type: patient.rule_type || "percent",
                // Convert paise to rupees for display if flat type
                fee_value: patient.fee_value
                    ? (patient.rule_type === 'flat' ? toRupees(patient.fee_value).toString() : patient.fee_value.toString())
                    : "",
                date_of_birth: patient.date_of_birth || "",
                address: patient.address || "",
                ageInputMode: patient.date_of_birth ? "dob" : "age"
            });
        }
    }, [patient]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let finalReferralId = formData.referral_doctor_id;

            // Handle new referral doctor creation
            if (!finalReferralId && formData.referral_doctor_name) {
                // Check if a doctor with this phone already exists locally
                const existingDoctor = formData.referral_doctor_phone
                    ? await db.users.where('phone').equals(formData.referral_doctor_phone).first()
                    : null;

                if (existingDoctor) {
                    finalReferralId = existingDoctor.id;
                } else {
                    // Create new referral doctor
                    finalReferralId = crypto.randomUUID();
                    const newDoctor = {
                        id: finalReferralId,
                        name: formData.referral_doctor_name,
                        phone: formData.referral_doctor_phone,
                        email: `placeholder_${finalReferralId}@clinicnode.com`,
                        is_registered: false,
                        created_by_user_id: userId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        _synced: false
                    };

                    await db.users.add(newDoctor);
                    await syncService.addToQueue('create', 'referral-doctor', {
                        id: finalReferralId,
                        name: formData.referral_doctor_name,
                        phone: formData.referral_doctor_phone
                    });
                }
            }

            const nowIso = new Date().toISOString();
            const updatedPatient = {
                name: formData.name,
                phone: formData.phone,
                email: formData.email,
                gender: formData.gender,
                notes: formData.notes,
                address: formData.address || null,
                referral_doctor_id: finalReferralId || null,
                rule_type: finalReferralId ? formData.rule_type : null,
                // Convert to paise if flat amount
                fee_value: finalReferralId && formData.fee_value
                    ? (formData.rule_type === 'flat' ? toPaise(formData.fee_value) : parseFloat(formData.fee_value))
                    : null,
                updated_at: nowIso,
                _synced: false
            };

            if (formData.ageInputMode === "age" && formData.age) {
                updatedPatient.age = parseInt(formData.age);
                if (patient.age !== updatedPatient.age || !patient.ageRecordedOn) {
                    updatedPatient.ageRecordedOn = nowIso;
                } else {
                    updatedPatient.ageRecordedOn = patient.ageRecordedOn;
                }
                updatedPatient.date_of_birth = null;
            } else if (formData.ageInputMode === "dob" && formData.date_of_birth) {
                updatedPatient.age = null;
                updatedPatient.ageRecordedOn = null;
                updatedPatient.date_of_birth = formData.date_of_birth;
            } else {
                updatedPatient.age = null;
                updatedPatient.ageRecordedOn = null;
                updatedPatient.date_of_birth = null;
            }

            await db.patients.update(patient.id, updatedPatient);
            await syncService.addToQueue('update', 'patient', {
                id: patient.id,
                ...updatedPatient
            });

            toast.success("Patient updated successfully!");
            onOpenChange(false);
        } catch (error) {
            console.error(error);
            toast.error("Failed to update patient");
        }
    };



    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="edit-patient-dialog"
                style={{
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <DialogHeader>
                    <DialogTitle>Edit Patient</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} style={{ overflow: 'auto', flex: 1, paddingBottom: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <Label htmlFor="name">Patient Name</Label>
                            <Input
                                id="name"
                                data-testid="edit-patient-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                                id="phone"
                                data-testid="edit-patient-phone"
                                value={formData.phone}
                                readOnly
                                disabled
                                className="bg-slate-100"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="edit-patient-email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="patient@example.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="gender">Gender</Label>
                            <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                                <SelectTrigger data-testid="edit-patient-gender">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="male">Male</SelectItem>
                                    <SelectItem value="female">Female</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', gridColumn: '1 / -1' }}>
                            <Label>Age / Date of Birth (Optional)</Label>
                            <div style={{ display: 'flex', background: '#f1f5f9', padding: '0.3rem', borderRadius: '8px', width: '100%', border: '1px solid #e2e8f0' }}>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, ageInputMode: 'age', date_of_birth: '' })}
                                    style={{
                                        flex: 1,
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        background: formData.ageInputMode === 'age' ? 'white' : 'transparent',
                                        boxShadow: formData.ageInputMode === 'age' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: formData.ageInputMode === 'age' ? '#0ea5e9' : '#64748b',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    Age
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, ageInputMode: 'dob', age: '' })}
                                    style={{
                                        flex: 1,
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        background: formData.ageInputMode === 'dob' ? 'white' : 'transparent',
                                        boxShadow: formData.ageInputMode === 'dob' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: formData.ageInputMode === 'dob' ? '#0ea5e9' : '#64748b',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    Date of Birth
                                </button>
                            </div>
                            
                            {formData.ageInputMode === 'age' ? (
                                <Input
                                    id="age"
                                    data-testid="edit-patient-age"
                                    type="number"
                                    value={formData.age}
                                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                    placeholder="Enter age in years"
                                />
                            ) : (
                                <Input
                                    id="date_of_birth"
                                    data-testid="edit-patient-dob"
                                    type="date"
                                    value={formData.date_of_birth}
                                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                />
                            )}
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Label htmlFor="address">Address (Optional)</Label>
                            <Input
                                id="address"
                                data-testid="edit-patient-address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="Enter patient address"
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Label htmlFor="notes">Notes</Label>
                            <Input
                                id="notes"
                                data-testid="edit-patient-notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>

                        {/* Referral Information Section Removed */}
                    </div>
                </form>

                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    paddingBottom: '0.5rem',
                    borderTop: '1px solid #e2e8f0',
                    flexShrink: 0,
                    background: 'white',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10
                }}>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        style={{ flex: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="save-patient-button"
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();
                            const form = document.querySelector('[data-testid="edit-patient-dialog"] form');
                            if (form) form.requestSubmit();
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            flex: 1
                        }}
                    >
                        Save Changes
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

PatientEditDialog.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    patient: PropTypes.object,
    userId: PropTypes.string
};
